// src/services/adminTruthService.ts
//
// Ch76 wiring · client for GET /analytics/admin-truth
// (backend/api/routes/analytics.py -> backend/api/services/admin_truth.py).
//
// The endpoint shipped with ZERO frontend consumers; every money figure it
// covers was previously quoted by a human typing SQL. This service is the
// first consumer. The contract is the ENVELOPE, not the number: a metric that
// could not be derived arrives as {status: "unknown", value: null} and MUST
// render as unknown — never as a confident zero. See the SILENT ZEROS note in
// admin_truth.py for why that rule exists.

import { fetchWithAuth } from "../lib/api";

export type AdminTruthStatus = "ok" | "unknown";

export interface AdminTruthEnvelope {
  /** The derived figure, or null when status is "unknown". */
  value: number | null;
  status: AdminTruthStatus;
  /** Versioned definition id — WHICH definition produced this number. */
  definition_id: string;
  computed_at: string;
  note?: string;
}

/**
 * Ch76 · the "snapshotted daily" half of the DoD MONEY instrument, carried on
 * the same read (backend/api/services/admin_truth_snapshot.py
 * latest_snapshot_status). `today_exists` is null under status "unknown":
 * the backend could not look, which is not the same as looking and finding
 * nothing.
 */
export interface AdminTruthSnapshot {
  status: AdminTruthStatus;
  today: string;
  latest_date: string | null;
  today_exists: boolean | null;
  note?: string;
}

export interface AdminTruthResponse {
  metrics: Record<string, AdminTruthEnvelope>;
  filters: Record<string, unknown>;
  generated_at: string;
  unknown_metrics: string[];
  all_known: boolean;
  /** Absent on a backend older than PR #468; rendered as unknown, never as "no snapshot". */
  snapshot?: AdminTruthSnapshot;
}

export async function fetchAdminTruth(): Promise<AdminTruthResponse> {
  const res = await fetchWithAuth<AdminTruthResponse>("/analytics/admin-truth");
  // Same rule as clientQueueService (PR #454 review Q1): the service is the
  // shape guard. A 200 whose body is not the contract must become a rejection
  // here, not a render-time throw.
  if (!res || typeof res !== "object" || typeof (res as any).metrics !== "object" || (res as any).metrics === null) {
    throw new Error("Malformed admin-truth response: `metrics` is not an object");
  }
  return res;
}
